import { ChainType } from "@0xsquid/squid-types";
import BigNumber from "bignumber.js";
import {
  CantonConnector,
  CantonHolding,
  CantonInstrument,
} from "../../core/types/canton";
import {
  CantonWallet,
  ConnectorID,
  WindowWalletFlag,
} from "../../core/types/wallet";

/**
 * Format a Canton connector into a Squid {@link CantonWallet}. Shared by the
 * announce-discovery and injected-provider paths.
 */
export function formatCantonWallet({
  connectorId,
  name,
  icon,
  connector,
}: {
  connectorId: ConnectorID;
  name: string;
  icon: string | undefined;
  connector: CantonConnector;
}): CantonWallet {
  return {
    name,
    connectorId,
    connectorName: name,
    icon,
    windowFlag: connectorId as unknown as WindowWalletFlag,
    skipInstallCheck: true,
    isInstalled: () => true,
    isMultiChain: false,
    type: ChainType.CANTON,
    connector,
  };
}

/**
 * Append injected wallets that aren't already covered by an announce-discovered
 * wallet with the same connectorId (in case a wallet later does both).
 */
export function mergeInjectedCantonWallets(
  announced: CantonWallet[],
  injected: CantonWallet[]
): CantonWallet[] {
  const seen = new Set(announced.map((w) => w.connectorId));
  return [...announced, ...injected.filter((w) => !seen.has(w.connectorId))];
}

// Canton PartyID = address
export const CANTON_PARTY_ID_SEPARATOR = "::";

/** Canton Coin's well-known token-standard instrument id (its address carries no id prefix). */
export const CANTON_AMULET_INSTRUMENT_ID = "Amulet";

export function isCantonAddressValid(address: string): boolean {
  // Canton address format: `name::fingerprint`, where name is an arbitrary string
  // and fingerprint is a 64-character hex string with a 1220 prefix (SHA-256 hash of the public key)
  const parts = address.split(CANTON_PARTY_ID_SEPARATOR);
  if (parts.length !== 2) return false;
  const [name, fingerprint] = parts;
  return name.length > 0 && /^1220[0-9a-f]{64}$/.test(fingerprint);
}

/**
 * Splice token-standard Holding interface id. A party's balance for an
 * instrument is the sum of `amount` across active contracts implementing this interface.
 */
export const CANTON_HOLDING_INTERFACE_ID =
  "#splice-api-token-holding-v1:Splice.Api.Token.HoldingV1:Holding";

/**
 * Derive the token-standard instrument `{ admin, id }` from a Squid Canton token
 * address (use the case-sensitive `originalAddress`), or `null` when the address
 * is not a valid Canton token address.
 *
 * The admin party is the last two `::` segments (`name::fingerprint`); the
 * instrument id is everything before that. Canton Coin has no id prefix
 * (`DSO::1220…`) and uses the well-known id `Amulet`.
 *   - `DSO::1220…`                                  -> { admin: "DSO::1220…", id: "Amulet" }
 *   - `USDCx::decentralized-usdc-…::12208…`         -> { admin: "decentralized-usdc-…::12208…", id: "USDCx" }
 */
export function parseCantonInstrument(
  address: string
): CantonInstrument | null {
  const segments = address.split(CANTON_PARTY_ID_SEPARATOR);
  if (segments.length < 2) {
    return null;
  }
  const admin = segments.slice(-2).join(CANTON_PARTY_ID_SEPARATOR);
  const idPrefix = segments.slice(0, -2).join(CANTON_PARTY_ID_SEPARATOR);
  return {
    admin,
    id: idPrefix.length > 0 ? idPrefix : CANTON_AMULET_INSTRUMENT_ID,
  };
}

/**
 * Body for `POST /v2/state/active-contracts`, filtered to the party's Holding
 * contracts with the interface view included (so we can read amount/instrument).
 *
 * ACS: Active Contract Set — the ledger's currently-active contracts (created and
 * not yet archived) as of a ledger offset.
 */
export function buildHoldingsAcsRequestBody(
  partyId: string,
  activeAtOffset: string
) {
  return {
    activeAtOffset,
    eventFormat: {
      verbose: true,
      filtersByParty: {
        [partyId]: {
          cumulative: [
            {
              identifierFilter: {
                InterfaceFilter: {
                  value: {
                    interfaceId: CANTON_HOLDING_INTERFACE_ID,
                    includeInterfaceView: true,
                    includeCreatedEventBlob: false,
                  },
                },
              },
            },
          ],
        },
      },
    },
  };
}

/**
 * Parse the party's Holding contracts (with contract ids) from a raw
 * `/v2/state/active-contracts` response
 *  Defensive: skips entries it can't parse.
 */
export function parseCantonHoldings(acsResponse: unknown): CantonHolding[] {
  if (!Array.isArray(acsResponse)) return [];

  const holdings: CantonHolding[] = [];

  for (const entry of acsResponse) {
    const createdEvent = (entry as any)?.contractEntry?.JsActiveContract
      ?.createdEvent;
    const contractId = createdEvent?.contractId;
    const views = createdEvent?.interfaceViews;
    if (!contractId || !Array.isArray(views)) continue;

    for (const view of views) {
      const value = view?.viewValue;
      const instrumentId = value?.instrumentId;
      if (
        !instrumentId?.admin ||
        instrumentId.id == null ||
        value.amount == null
      ) {
        continue;
      }
      holdings.push({
        contractId,
        instrument: { admin: instrumentId.admin, id: instrumentId.id },
        amount: String(value.amount),
        // Locked holdings are not spendable, skip them
        locked: value.lock != null,
      });
    }
  }

  return holdings;
}

/**
 * Key used to group holdings by instrument.
 */
function instrumentKey(admin: string, id: string): string {
  return `${admin}|${id}`;
}

export function cantonInstrumentKey(instrument: CantonInstrument): string {
  return instrumentKey(instrument.admin, instrument.id);
}

/**
 * Group the (unlocked) Holding amounts by instrument from a raw
 * `/v2/state/active-contracts` response. Returns a map of instrument key
 * (`admin|id`) -> human-readable decimal string (the token-standard `amount`
 * is already a Decimal, not base units).
 */
export function groupCantonHoldingBalances(
  acsResponse: unknown
): Record<string, string> {
  const totals: Record<string, BigNumber> = {};

  for (const holding of parseCantonHoldings(acsResponse)) {
    // Locked holdings are not spendable, skip them
    if (holding.locked) continue;

    const key = cantonInstrumentKey(holding.instrument);
    const total = totals[key] ?? new BigNumber(0);

    totals[key] = total.plus(new BigNumber(holding.amount));
  }

  return Object.fromEntries(
    Object.entries(totals).map(([key, total]) => [key, total.toString(10)])
  );
}
